import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertCircle, CheckCircle2, Plus, Send } from 'lucide-react';
import { LoadingBlock } from '@/components/ui/spinner';
import { parseServerTime } from '@/lib/serverTime';
import {
  marketingTemplateApi,
  MarketingEmailTemplate,
  MarketingEmailTemplateRequest,
} from '@/api/marketingOutreachApi';

// ── 마케팅 아웃리치(홍보 이메일) 템플릿 관리 (2026-09-13 신설) ──────────────
// {{companyName}}/{{aiReason}} 플레이스홀더는 발송 시 실제 값으로 치환된다.
// active=true는 항상 1건만 유지되며, 배치는 그 1건만 사용한다.

const emptyForm: MarketingEmailTemplateRequest = { name: '', subject: '', body: '' };

export default function AdminMarketingEmailTemplates() {
  const [templates, setTemplates] = useState<MarketingEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<MarketingEmailTemplateRequest>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingTemplateApi.list();
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('템플릿 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (template: MarketingEmailTemplate) => {
    setEditingId(template.id);
    setForm({ name: template.name, subject: template.subject, body: template.body });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      setError('이름/제목/본문을 모두 입력해주세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await marketingTemplateApi.update(editingId, form);
        setActionMessage('템플릿이 수정되었습니다.');
      } else {
        await marketingTemplateApi.create(form);
        setActionMessage('템플릿이 생성되었습니다.');
      }
      setEditorOpen(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '템플릿 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (template: MarketingEmailTemplate) => {
    if (template.active) return;
    if (!window.confirm(`"${template.name}"을(를) 활성 템플릿으로 지정하시겠습니까? 다른 템플릿은 모두 비활성화됩니다.`)) {
      return;
    }
    setActivatingId(template.id);
    setError(null);
    try {
      await marketingTemplateApi.activate(template.id);
      setActionMessage(`"${template.name}"이(가) 활성화되었습니다.`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? '템플릿 활성화에 실패했습니다.');
    } finally {
      setActivatingId(null);
    }
  };

  const formatDateTime = (iso: string) => {
    const date = parseServerTime(iso);
    return date
      ? date.toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '-';
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="발송 이메일 템플릿 관리"
        description="마케팅 아웃리치(홍보 이메일) 제목/본문을 관리합니다. 본문에는 {{companyName}}(회사명), {{aiReason}}(AI가 작성한 발송 사유) 플레이스홀더를 쓸 수 있습니다. 활성 템플릿은 항상 1개만 유지되며, 매일 발송 배치는 그 템플릿만 사용합니다."
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            새 템플릿
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {actionMessage && (
        <Alert>
          <AlertDescription>{actionMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-center">활성</TableHead>
                    <TableHead>이름</TableHead>
                    <TableHead>제목</TableHead>
                    <TableHead>수정일</TableHead>
                    <TableHead className="text-center">관리</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-400 py-16 text-sm">
                        등록된 템플릿이 없습니다. "새 템플릿"으로 먼저 하나 만들어주세요.
                      </TableCell>
                    </TableRow>
                  ) : templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="text-center">
                        {template.active ? (
                          <Badge className="bg-green-100 text-green-700 border border-green-200 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            활성
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-medium">{template.name}</TableCell>
                      <TableCell className="text-xs text-gray-600 max-w-md truncate">{template.subject}</TableCell>
                      <TableCell className="text-xs text-gray-500">{formatDateTime(template.updatedAt)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => openEdit(template)}>
                            수정
                          </Button>
                          {!template.active && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              disabled={activatingId === template.id}
                              onClick={() => handleActivate(template)}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              {activatingId === template.id ? '활성화 중...' : '활성화'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? '템플릿 수정' : '새 템플릿'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">이름</label>
              <Input
                placeholder="예: 기본 템플릿"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">제목</label>
              <Input
                placeholder="(광고) {{companyName}}, ..."
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">본문</label>
              <textarea
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                rows={14}
                placeholder={'안녕하세요, {{companyName}} 담당자님.\n\n{{aiReason}}\n\n...'}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                {'{{companyName}}'} = 회사명, {'{{aiReason}}'} = AI가 작성한 발송 사유(1~2문장)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
